import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Table2 } from 'lucide-react';
import { api, ApiError } from '../api/client';
import type { ColumnInfo, Connection } from '../api/client';
import { Alert, EmptyState, Spinner } from '../components/Feedback';
import { StatusPill } from './DashboardPage';

/** Schema browser for one database. */
export function DatabaseDetailPage() {
  const { id = '' } = useParams();

  const [connection, setConnection] = useState<Connection | null>(null);
  const [tables, setTables] = useState<string[] | null>(null);
  const [views, setViews] = useState<string[]>([]);
  const [selected, setSelected] = useState('');
  const [columns, setColumns] = useState<ColumnInfo[] | null>(null);
  const [error, setError] = useState('');
  const [loadingColumn, setLoadingColumn] = useState(false);

  useEffect(() => {
    api.databases
      .get(id)
      .then(setConnection)
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Not found.'));
    api.databases
      .tables(id)
      .then((data) => {
        setTables(data.tables);
        setViews(data.views ?? []);
        if (data.tables.length > 0) setSelected(data.tables[0]);
      })
      .catch((e) =>
        setError(e instanceof ApiError ? e.message : 'The schema could not be read.'),
      );
  }, [id]);

  useEffect(() => {
    if (!selected) return;
    setLoadingColumn(true);
    setColumns(null);
    api.databases
      .columns(id, selected)
      .then((data) => setColumns(data.columns))
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Could not read the table.'))
      .finally(() => setLoadingColumn(false));
  }, [id, selected]);

  return (
    <div className="page">
      <Link className="link back" to="/databases">
        <ArrowLeft size={16} /> Databases
      </Link>

      <header className="page-head">
        <div>
          <p className="eyebrow">SCHEMA</p>
          <h1>{connection?.name ?? 'Database'}</h1>
          {connection && (
            <p className="muted">
              {connection.database_type} · {connection.database_name}
            </p>
          )}
        </div>
        {connection && (
          <div className="head-actions">
            <StatusPill status={connection.status} />
            <Link className="primary" to={`/query?connection=${connection.id}`}>
              Query
            </Link>
          </div>
        )}
      </header>

      {error && <Alert tone="error" onDismiss={() => setError('')}>{error}</Alert>}

      {tables === null ? (
        <Spinner label="Reading schema" />
      ) : tables.length === 0 && views.length === 0 ? (
        <EmptyState
          title="No tables found"
          description="This account can see no tables in that database."
        />
      ) : (
        <div className="schema-layout">
          <aside className="panel">
            <h3>
              <Table2 size={17} /> Tables
              <span>{tables.length + views.length}</span>
            </h3>
            <div className="table-list">
              {tables.map((table) => (
                <button
                  key={table}
                  className={selected === table ? 'active' : ''}
                  onClick={() => setSelected(table)}
                >
                  {table}
                </button>
              ))}
              {views.length > 0 && (
                <>
                  <p className="list-label">Views</p>
                  {views.map((view) => (
                    <button
                      key={view}
                      className={selected === view ? 'active' : ''}
                      onClick={() => setSelected(view)}
                    >
                      {view}
                    </button>
                  ))}
                </>
              )}
            </div>
          </aside>

          <section className="panel">
            <h3>{selected}</h3>
            {loadingColumn ? (
              <Spinner label="Reading columns" />
            ) : columns ? (
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Column</th>
                      <th>Type</th>
                      <th>Null</th>
                      <th>Key</th>
                      <th>References</th>
                    </tr>
                  </thead>
                  <tbody>
                    {columns.map((column) => (
                      <tr key={column.name}>
                        <td>
                          <code>{column.name}</code>
                        </td>
                        <td className="muted">{column.type}</td>
                        <td>{column.nullable ? 'yes' : 'no'}</td>
                        <td>
                          {column.primary_key ? (
                            <span className="badge">PK</span>
                          ) : column.unique ? (
                            <span className="badge">unique</span>
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </td>
                        <td className="muted">
                          {column.references.length > 0 ? column.references.join(', ') : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </section>
        </div>
      )}
    </div>
  );
}
